import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSubscriptionPlans, SubscriptionPlan } from "../api/subscription";
import { FiCheck, FiStar, FiZap, FiAward } from "react-icons/fi";

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getSubscriptionPlans();
        // Sort plans: STARTER, PRO, ELITE
        const sortedPlans = data.sort((a, b) => {
          const order = { STARTER: 1, PRO: 2, ELITE: 3 };
          return (order[a.planTier] || 0) - (order[b.planTier] || 0);
        });
        setPlans(sortedPlans);
      } catch (err: any) {
        setError(err?.message || "Failed to load subscription plans");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getPlanIcon = (tier: string) => {
    switch (tier) {
      case "ELITE":
        return <FiAward size={32} />;
      case "PRO":
        return <FiZap size={32} />;
      default:
        return <FiStar size={32} />;
    }
  };

  const getPlanGradient = (tier: string) => {
    switch (tier) {
      case "ELITE":
        return "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)";
      case "PRO":
        return "linear-gradient(135deg, #6C63FF 0%, #9D8DF1 100%)";
      default:
        return "linear-gradient(135deg, #4A90E2 0%, #6BB6FF 100%)";
    }
  };

  const handleGetStarted = (plan: SubscriptionPlan) => {
    // Check if user is logged in
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      // Redirect to login with return path
      navigate("/login?redirect=/subscription");
      return;
    }

    // Check if user is a vendor (they should use the vendor portal)
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (user?.userType?.toUpperCase() === "VENDOR") {
        // Vendors should use the vendor portal
        window.location.href = "https://vendor.eventnova.com.ng";
        return;
      }
    } catch (e) {
      // Continue if user parsing fails
    }

    // For clients, redirect to vendor portal to subscribe
    window.location.href = "https://vendor.eventnova.com.ng";
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 16px 40px",
        }}
      >
        <div
          style={{
            color: "#fff",
            fontSize: 18,
            textAlign: "center",
          }}
        >
          Loading subscription plans...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 16px 40px",
        }}
      >
        <div
          style={{
            color: "#fecaca",
            fontSize: 16,
            textAlign: "center",
            maxWidth: 500,
            background: "rgba(248,113,113,0.16)",
            padding: "20px 24px",
            borderRadius: 12,
            border: "1px solid rgba(248,113,113,0.7)",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
        padding: "80px 16px 40px",
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <h1
            style={{
              fontSize: "clamp(32px, 5vw, 48px)",
              fontWeight: 800,
              color: "#fff",
              marginBottom: 16,
              background:
                "linear-gradient(135deg, #fff 0%, #c7d2fe 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Become a Vendor Partner
          </h1>
          <p
            style={{
              fontSize: "clamp(16px, 2vw, 20px)",
              color: "rgba(199,210,254,0.95)",
              maxWidth: 700,
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            Join EventNova's network of premium event vendors and grow your
            business. Choose the plan that fits your needs.
          </p>
        </div>

        {/* Plans Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 32,
            marginBottom: 40,
          }}
        >
          {plans
            .filter((plan) => plan.isActive)
            .map((plan) => (
              <div
                key={plan.id}
                style={{
                  background:
                    plan.planTier === "ELITE"
                      ? "linear-gradient(145deg, rgba(255,215,0,0.15) 0%, rgba(255,165,0,0.1) 100%)"
                      : "linear-gradient(145deg, rgba(108,99,255,0.15) 0%, rgba(157,141,241,0.1) 100%)",
                  borderRadius: 24,
                  padding: 32,
                  border:
                    plan.planTier === "ELITE"
                      ? "2px solid rgba(255,215,0,0.5)"
                      : "1px solid rgba(108,99,255,0.3)",
                  boxShadow:
                    plan.planTier === "ELITE"
                      ? "0 20px 60px rgba(255,215,0,0.2)"
                      : "0 10px 40px rgba(0,0,0,0.2)",
                  position: "relative",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow =
                    plan.planTier === "ELITE"
                      ? "0 30px 80px rgba(255,215,0,0.3)"
                      : "0 20px 60px rgba(108,99,255,0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    plan.planTier === "ELITE"
                      ? "0 20px 60px rgba(255,215,0,0.2)"
                      : "0 10px 40px rgba(0,0,0,0.2)";
                }}
              >
                {plan.planTier === "ELITE" && (
                  <div
                    style={{
                      position: "absolute",
                      top: -12,
                      right: 24,
                      background: getPlanGradient(plan.planTier),
                      color: "#000",
                      padding: "6px 16px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Most Popular
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      color: plan.planTier === "ELITE" ? "#FFD700" : "#6C63FF",
                    }}
                  >
                    {getPlanIcon(plan.planTier)}
                  </div>
                  <h2
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: "#fff",
                      margin: 0,
                    }}
                  >
                    {plan.name}
                  </h2>
                </div>

                <p
                  style={{
                    color: "rgba(199,210,254,0.9)",
                    fontSize: 14,
                    marginBottom: 24,
                    lineHeight: 1.6,
                  }}
                >
                  {plan.description}
                </p>

                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      fontSize: 42,
                      fontWeight: 800,
                      color: "#fff",
                      marginBottom: 4,
                    }}
                  >
                    {formatPrice(plan.price)}
                  </div>
                  <div
                    style={{
                      color: "rgba(199,210,254,0.8)",
                      fontSize: 14,
                    }}
                  >
                    per {plan.durationInDays} days
                  </div>
                </div>

                <button
                  onClick={() => handleGetStarted(plan)}
                  style={{
                    width: "100%",
                    padding: "14px 24px",
                    borderRadius: 12,
                    border: "none",
                    fontSize: 16,
                    fontWeight: 700,
                    color: plan.planTier === "ELITE" ? "#000" : "#fff",
                    background: getPlanGradient(plan.planTier),
                    cursor: "pointer",
                    marginBottom: 24,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 30px rgba(0,0,0,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.2)";
                  }}
                >
                  Get Started
                </button>

                <div>
                  <div
                    style={{
                      color: "#c7d2fe",
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 16,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Features
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {plan.features.map((feature, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            color: plan.planTier === "ELITE" ? "#FFD700" : "#6C63FF",
                            marginTop: 2,
                            flexShrink: 0,
                          }}
                        >
                          <FiCheck size={18} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              color: "#fff",
                              fontSize: 14,
                              fontWeight: 500,
                              marginBottom: 2,
                            }}
                          >
                            {feature.key !== "Description" ? feature.key : ""}
                          </div>
                          <div
                            style={{
                              color: "rgba(199,210,254,0.9)",
                              fontSize: 13,
                              lineHeight: 1.5,
                            }}
                          >
                            {feature.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Footer Note */}
        <div
          style={{
            textAlign: "center",
            color: "#fff",
            fontSize: 15,
            maxWidth: 800,
            margin: "0 auto",
            padding: "28px 32px",
            background: "rgba(15,23,42,0.85)",
            borderRadius: 16,
            border: "2px solid rgba(108,99,255,0.5)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}
        >
          <p style={{ margin: 0, lineHeight: 1.7, color: "#fff" }}>
            <strong style={{ color: "#fff", fontSize: 16 }}>Note:</strong> Vendors must log in
            to the vendor portal to complete subscription. If you're already a
            vendor, please visit{" "}
            <a
              href="https://vendor.eventnova.com.ng"
              style={{
                color: "#9D8DF1",
                textDecoration: "none",
                fontWeight: 700,
                borderBottom: "1px solid #9D8DF1",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#9D8DF1";
              }}
            >
              vendor.eventnova.com.ng
            </a>{" "}
            to manage your subscription.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;

